// The dates a fold row's range is composed over — three field names any
// caller's own section/card shape can satisfy structurally, without a
// mapping step at the call site (t021 `impl-fold-row`).
export interface FoldRangeDate {
  dayOfMonth: string
  monthAbbr: string
  date: string
}

// The fold row's range span, composed client-side from a folded run's own
// day-numeral/month-abbreviation fields — no count, no plural form, no
// server-sent string. Same-month is decided on the ISO date keys' own
// year-month prefix, not on the rendered abbreviation, so two same-named
// months in different years (or different months sharing an abbreviation)
// never collapse. `dates` is never empty where this is called — every
// caller only renders the row once its own folded run is non-empty.
export function pastRangeText(dates: FoldRangeDate[]): string {
  const first = dates[0]
  const last = dates[dates.length - 1]

  if (first.date === last.date) return `${first.dayOfMonth} ${first.monthAbbr}`
  if (first.date.slice(0, 7) === last.date.slice(0, 7)) {
    return `${first.dayOfMonth} – ${last.dayOfMonth} ${last.monthAbbr}`
  }

  return `${first.dayOfMonth} ${first.monthAbbr} – ${last.dayOfMonth} ${last.monthAbbr}`
}

interface FoldRowProps {
  open: boolean
  onToggle: () => void
  dates: FoldRangeDate[]
  // Neutral names, not per-domain locale keys: each page passes its own
  // pair of strings (e.g. `schedule.past_days`/`schedule.hide_past` or
  // `flights.past_flights`/`flights.hide_past`) rather than this module
  // fixing a shared key pair.
  labels: { collapsed: string; expanded: string }
}

// A disclosure button that folds a run of past items behind one row: a
// chevron that flips with `open`, `aria-expanded` reflecting the state, and
// the composed range shown only while collapsed. Renders the `<button>`
// alone — no wrapper element, no state of its own — so the page that places
// it decides both what wraps it and when it mounts at all.
export default function FoldRow({ open, onToggle, dates, labels }: FoldRowProps) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="flex min-h-11 w-full items-center gap-2.5 rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-left text-sm text-gray-500"
    >
      <svg
        aria-hidden="true"
        className="text-gray-400"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d={open ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="font-semibold">{open ? labels.expanded : labels.collapsed}</span>
      {/* The range renders only in the collapsed state — the expanded
          button carries no range span at all. */}
      {!open && <span className="text-gray-400">{`· ${pastRangeText(dates)}`}</span>}
    </button>
  )
}
